import { useCallback } from 'react';
import { MessageType } from '../../types/message';
import { ChatType } from '../../api/chats';
import { PlayableResult } from '../../components/playables-catalog';

interface UseEditProps {
  chatObj: ChatType | null;
  setChatObj: (chat: ChatType) => void;
  messages: MessageType[];
  updateMessages: (messages: MessageType[]) => void;
  activePlayable: PlayableResult | null;
  setIsPendingAiResponse: (isPending: boolean) => void;
}

export const useEdit = ({
  chatObj,
  setChatObj,
  messages,
  updateMessages,
  activePlayable,
  setIsPendingAiResponse
}: UseEditProps) => {
  
  const handleEdit = useCallback(async (messageId: string, content: string) => {
    if (!chatObj) {
      throw new Error("Cannot edit message: Chat object not available");
    }
    
    try {
      const updatedChat = await chatObj.editMessage(messageId, content);
      setChatObj(updatedChat);
      
      // Update the specific message in the messages array
      updateMessages(messages.map(msg => 
        msg.id === messageId 
          ? { ...msg, content } 
          : msg
      ));

      // Only generate if we edited a user message AND it was the last message
      const editedMessage = messages.find(msg => msg.id === messageId);
      const isLastMessage = messages[messages.length - 1].id === messageId;
      
      if (editedMessage?.member_type === 'user' && isLastMessage) {
        setIsPendingAiResponse(true);
        try {
          // Type casting as in the original code
          const finalChat = await updatedChat.generate(activePlayable?.id as any);
          setChatObj(finalChat);
          updateMessages(finalChat.messages || []);
        } finally {
          setIsPendingAiResponse(false);
        }
      }
    } catch (err) {
      console.error('Failed to edit message:', err);
      throw err; // Re-throw to let the caller handle the error
    }
  }, [
    chatObj,
    setChatObj,
    messages,
    updateMessages,
    activePlayable,
    setIsPendingAiResponse
  ]);

  return { handleEdit };
};

export default useEdit; 